/**
 * Turns a backend refusal into something a screen can say.
 *
 * The call ceiling used to surface as `bombing prevention: lead has already
 * received 3 calls from this agent in the last 24h (max 3)` — internal
 * vocabulary, in English, inside a Spanish interface. In the trigger sheet it
 * was worse than untranslated: there was no error handler at all, so a refused
 * call did nothing at all (FLOSEAI-679).
 *
 * This resolves a key and the values its copy needs, and never a sentence. The
 * wording belongs in the translation files with the rest of the product's
 * voice, and the operator message must never reach a screen.
 */

export interface CallRefusal {
  key: string;
  values?: Record<string, string | number>;
}

/** Shape Nest produces, whether the body is flat or wrapped under `message`. */
function refusalBody(error: unknown): Record<string, unknown> | undefined {
  const data = (error as { response?: { data?: unknown } })?.response?.data;
  if (!data || typeof data !== 'object') return undefined;

  const flat = data as Record<string, unknown>;
  if (typeof flat.code === 'string') return flat;

  // `new ConflictException({ code, ... })` arrives nested under `message`.
  const nested = flat.message;
  if (nested && typeof nested === 'object' && typeof (nested as { code?: unknown }).code === 'string') {
    return nested as Record<string, unknown>;
  }

  return undefined;
}

export function describeCallRefusal(error: unknown): CallRefusal {
  const body = refusalBody(error);
  const code = body?.code;

  if (code === 'CALL_DAILY_LIMIT_REACHED') {
    const used = Number(body?.used ?? 0);
    const limit = Number(body?.limit ?? 0);
    const freesAt = typeof body?.freesAt === 'string' ? body.freesAt : undefined;

    // A missing horizon needs its own sentence, not the same one with a blank
    // where the time should be.
    return freesAt
      ? { key: 'voiceAgents.refusal.dailyLimit', values: { used, limit, freesAt } }
      : { key: 'voiceAgents.refusal.dailyLimitNoHorizon', values: { used, limit } };
  }

  if (code === 'LEAD_DO_NOT_CALL') {
    return { key: 'voiceAgents.refusal.doNotCall' };
  }

  if (code === 'CONSENT_REFUSED') {
    return { key: 'voiceAgents.refusal.consentRefused' };
  }

  // Anything unrecognised still says something, and still says it in the
  // user's language. Passing the backend text through is the exact failure
  // this ticket describes.
  return { key: 'voiceAgents.refusal.generic' };
}
